'use client';
import React, { useEffect, useState, useCallback, Suspense, useRef, useTransition } from 'react';
import { Box, Button, Grid, Typography, Chip } from '@mui/material';
import { PricingRate, Venue, VenueBookedTimeSlot } from '../../../shared/models';
import { SearchNavBar } from '../components/search_navbar';
import MapIcon from '@mui/icons-material/Map';
import ListIcon from '@mui/icons-material/List';
import BetterMap from '../components/better_map';
import LoadingLogo from '../components/loading_logo';
import VenueCardList from '../components/venue_card_list';
import { useSearchSettingsStore } from '../stores/search_settings_store';
import { getBackendURL } from '../lib/util';
import { useRouter } from 'next/navigation';
import { debounce } from 'lodash';
import { trackEvent } from '../lib/analytics';

interface SearchParams {
  date?: string;
  time?: string;
  people?: number;
  attributes?: string[];
  priceRange?: [number, number];
  minPrice?: number;
  maxPrice?: number;
  zipCode?: string;
  neighborhoods?: string[];
  privacyLevel?: 'private' | 'semi-private' | 'non-private';
}


function SearchContent(props: { searchParams: SearchParams }) {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [venueBookedTimeSlots, setVenueBookedTimeSlots] = useState<Record<string, Record<string, VenueBookedTimeSlot[]>> | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
  const [mapCenter, setMapCenter] = useState<[number, number] | undefined>(
    undefined
  );
  const [mapZoom, setMapZoom] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [hoveredVenueId, setHoveredVenueId] = useState<string | null>(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [allVenues, setAllVenues] = useState<Venue[]>([]);
  const [venuePrices, setVenuePrices] = useState<Record<string, number> | null>(null);
  const [hoveredVenue, setHoveredVenue] = useState<Venue | null>(null);
  const [tempHoveredVenueId, setTempHoveredVenueId] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();
  const [isLoadingFullResults, setIsLoadingFullResults] = useState<boolean>(false);
  const [initialVenuesLoaded, setInitialVenuesLoaded] = useState<boolean>(false);
  const fullVenuesDataRef = useRef<any>(null);

  // New state for map-filtered venues
  const [visibleVenueIds, setVisibleVenueIds] = useState<string[]>([]);
  // Always filter by map - set to true by default
  const [isMapFiltered, setIsMapFiltered] = useState<boolean>(true);
  // Initialize with empty array to match venues' initial state
  const [displayedVenues, setDisplayedVenues] = useState<Venue[]>([]);

  // Use refs to track initial load state
  const initialLoadRef = useRef(true);
  const isInitialFetchRef = useRef(true);
  const isFetchingRef = useRef(false);

  // Add ref to store last search parameters to avoid duplicate calls
  const lastSearchParamsRef = useRef<{
    date: string;
    time: string;
    people: string;
    attributes: string[];
    priceRange: string;
    zipCode: string;
    neighborhoods: string[];
    privacyLevel: string;
  }>({
    date: '',
    time: '',
    people: '',
    attributes: [] as string[],
    priceRange: '',
    zipCode: '',
    neighborhoods: [] as string[],
    privacyLevel: ''
  });

  const router = useRouter();

  // Use the search settings store
  const {
    selectedDate,
    priceRange,
    people,
    zipCode,
    selectedAttributes,
    selectedTime,
    initializeFromUrl,
    isDirty,
    neighborhoods,
    privacyLevel
  } = useSearchSettingsStore();

  // Track search events with additional parameters
  const trackSearch = useCallback((searchData: {
    date?: string;
    time?: string;
    people?: string;
    attributes?: string[];
    priceRange?: string;
    zipCode?: string;
    neighborhoods?: string[];
    privacyLevel?: string;
    venuesCount?: number;
    source?: 'initial' | 'filter-change' | 'map-filter';
  }) => {
    // Calculate days away from today if date is provided
    let daysAway: number | string = 'not-set';
    if (searchData.date) {
      const searchDate = new Date(searchData.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time part for accurate day calculation
      const timeDiff = searchDate.getTime() - today.getTime();
      daysAway = Math.floor(timeDiff / (1000 * 3600 * 24));
    }

    trackEvent('search_venues', {
      date: searchData.date || 'not-set',
      time: searchData.time || 'not-set',
      people: searchData.people || 'not-set',
      attributes: searchData.attributes?.join(',') || 'none',
      priceRange: searchData.priceRange || 'not-set',
      zipCode: searchData.zipCode || 'not-set',
      neighborhoods: searchData.neighborhoods?.join(',') || 'none',
      privacyLevel: searchData.privacyLevel || 'not-set',
      venuesCount: searchData.venuesCount || 0,
      source: searchData.source || 'not-specified',
      daysAway: daysAway
    });
  }, []);

  // Track venue interaction events
  const trackVenueInteraction = useCallback((action: string, venueId: string, venueName: string) => {
    trackEvent('venue_interaction', {
      action,
      venueId,
      venueName,
      date: selectedDate ? selectedDate.format('YYYY-MM-DD') : 'not-set',
      time: selectedTime || 'not-set',
      people: people || 'not-set',
      privacyLevel: privacyLevel || 'not-set'
    });
  }, [selectedDate, selectedTime, people, privacyLevel]);

  // Add new tracking for venue card click
  const trackVenueCardClick = useCallback((venue: Venue) => {
    trackEvent('venue_card_click', {
      venueId: venue.ID,
      venueName: venue.Name,
      date: selectedDate ? selectedDate.format('YYYY-MM-DD') : 'not-set',
      time: selectedTime || 'not-set',
      people: people || 'not-set'
    });
  }, [selectedDate, selectedTime, people]);

  // Replace the existing debouncedFetch function to include analytics tracking
  const debouncedFetch = useCallback(
    debounce(async (params: {
      date: string,
      time: string | null,
      people: string,
      attributes: string[],
      priceRange: string,
      zipCode: string,
      neighborhoods: string[],
      privacyLevel?: string,
      isInitialLoad?: boolean
    }, setController: (controller: AbortController | null) => void) => {
      // Skip if already fetching
      if (isFetchingRef.current && !params.isInitialLoad) {
        console.log('Skipping fetch - already in progress');
        setController(null);
        return;
      }

      // For non-initial loads, check if parameters are the same as last search
      const currentParams = JSON.stringify(params);
      const lastParams = JSON.stringify(lastSearchParamsRef.current);

      if (currentParams === lastParams && !params.isInitialLoad) {
        console.log('Skipping fetch - parameters unchanged');
        setController(null);
        return;
      }

      // Update last search parameters
      lastSearchParamsRef.current = {
        date: params.date || '',
        time: params.time || '',
        people: params.people || '',
        attributes: params.attributes || [],
        priceRange: params.priceRange || '',
        zipCode: params.zipCode || '',
        neighborhoods: params.neighborhoods || [],
        privacyLevel: params.privacyLevel || ''
      };

      // Set fetching flag
      isFetchingRef.current = true;
      setIsLoading(true);

      // Create an abort controller to handle cancellation
      const abortController = new AbortController();

      // Only set the controller if the function hasn't been executed in parallel
      if (!isFetchingRef.current) {
        setController(null);
        return;
      }

      setController(abortController);

      console.log('Making venue fetch due to parameter change');
      console.log('Fetching venues with params:', params);

      // Track search event
      trackSearch({
        date: params.date,
        time: params.time || undefined,
        people: params.people,
        attributes: params.attributes,
        priceRange: params.priceRange,
        zipCode: params.zipCode,
        neighborhoods: params.neighborhoods,
        privacyLevel: params.privacyLevel,
        source: params.isInitialLoad ? 'initial' : 'filter-change'
      });

      // Convert dayjs object to Date
      const currentDateValue = params.date ? new Date(params.date) : new Date();
      const partySizeValue = params.people ? parseInt(params.people) : 10;

      // Log the request parameters for debugging
      console.log('Sending search request with parameters:', {
        date: currentDateValue.toISOString().split('T')[0],
        time: params.time,
        people: partySizeValue,
        attributes: params.attributes,
        priceRange: params.priceRange ? params.priceRange.split('-').map(Number) : undefined,
        zipCode: params.zipCode,
        neighborhoods: params.neighborhoods,
        privacyLevel: params.privacyLevel
      });

      // Add a specific log for privacy level to debug
      console.log('Privacy level being sent in request:', params.privacyLevel);

      try {
        // Wrap the heavy computation in a startTransition to keep the UI responsive
        startTransition(async () => {
          const startTime = performance.now();

          // Check if the request has been aborted before making the fetch
          if (abortController.signal.aborted) {
            console.log('Request was aborted before fetch started');
            isFetchingRef.current = false;
            setIsLoading(false);
            return;
          }

          // Log the request parameters for debugging
          console.log('Sending search request with parameters:', {
            date: currentDateValue.toISOString().split('T')[0],
            time: params.time,
            people: partySizeValue,
            attributes: params.attributes,
            priceRange: params.priceRange ? params.priceRange.split('-').map(Number) : undefined,
            zipCode: params.zipCode,
            neighborhoods: params.neighborhoods,
            privacyLevel: params.privacyLevel
          });

          try {
            const response = await fetch(`${getBackendURL('searchVenues')}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                date: currentDateValue.toISOString().split('T')[0],
                time: params.time,
                people: partySizeValue,
                attributes: params.attributes,
                priceRange: params.priceRange ? params.priceRange.split('-').map(Number) : undefined,
                minPrice: params.priceRange ? Number(params.priceRange.split('-')[0]) : undefined,
                maxPrice: params.priceRange ? Number(params.priceRange.split('-')[1]) : undefined,
                zipCode: params.zipCode,
                neighborhoods: params.neighborhoods,
                privacyLevel: params.privacyLevel
              }),
              signal: abortController.signal
            });

            if (!response.ok) {
              throw new Error(`API error: ${response.status}`);
            }

            const backendResponse = await response.json();
            const endTime = performance.now();
            console.log(`Backend searchVenues took ${endTime - startTime} milliseconds`);

            // Check if the request was aborted while waiting for the response
            if (abortController.signal.aborted) {
              console.log('Request was aborted during processing');
              return;
            }

            // Use a single state update instead of multiple ones
            if (backendResponse.venues) {
              // Store the full data for later use
              fullVenuesDataRef.current = {
                allVenues: backendResponse.allVenues,
                venues: backendResponse.venues,
                venuePrices: backendResponse.venuePrices || null,
                venueBookedTimeSlots: backendResponse.venueBookedTimeSlots || null
              };

              // If privacy level is specified, filter out venues with zero prices
              // This ensures we only show venues with appropriate sections
              if (params.privacyLevel) {
                console.log(`Filtering venues based on privacy level: ${params.privacyLevel}`);

                // Check venuePrices for zero values (meaning no matching privacy level section)
                if (backendResponse.venuePrices) {
                  const venuesWithPrivacyMatches = backendResponse.venues.filter(venue => {
                    const price = backendResponse.venuePrices[venue.ID];
                    // Keep venue if it has a positive price (indicating it has a matching privacy level section)
                    return price > 0;
                  });

                  console.log(`Found ${venuesWithPrivacyMatches.length} venues matching privacy level ${params.privacyLevel} out of ${backendResponse.venues.length} total`);

                  // Update the fullVenuesDataRef with the filtered list
                  fullVenuesDataRef.current.venues = venuesWithPrivacyMatches;
                }
              }

              // Initially show only a subset of venues
              setAllVenues(fullVenuesDataRef.current.allVenues.slice(0, 3));
              setVenues(fullVenuesDataRef.current.venues.slice(0, 3));
              setVenuePrices(fullVenuesDataRef.current.venuePrices);
              setVenueBookedTimeSlots(fullVenuesDataRef.current.venueBookedTimeSlots);
              setInitialVenuesLoaded(true);

              // Track search results
              trackSearch({
                ...params,
                venuesCount: backendResponse.venues.length,
                source: params.isInitialLoad ? 'initial' : 'filter-change'
              });

              backendResponse.venues.forEach(venue => {
                // Check in pricing rates
                const noMinSpendSections = Object.entries(venue.PricingRatesPerSection || {})
                  .filter(([id, rate]) => {
                    // Use type assertion to handle the PricingRate type
                    const pricingRate = rate as PricingRate;
                    return pricingRate?.IsNoMinimumSpendOption === true;
                  })
                  .map(([id, rate]) => ({
                    id,
                    name: venue.Sections[id]?.Name,
                    capacity: venue.Sections[id]?.Capacity
                  }));

              });

              // Set map center based on first venue if available
              if (backendResponse.venues.length > 0 && backendResponse.venues[0].Position) {
                setMapCenter([
                  backendResponse.venues[0].Position.lat,
                  backendResponse.venues[0].Position.lng,
                ]);
              }
            }
          } catch (error) {
            // Handle fetch errors
            if ((error as any)?.name === 'AbortError') {
              console.log('Fetch was aborted');
            } else {
              console.error('Error fetching venues:', error);

              // Track error event
              trackEvent('search_error', {
                error: (error as Error).message,
                date: params.date,
                time: params.time || 'not-set',
                people: params.people
              });
            }
          }

          // If this was the initial load, mark initial fetch as complete
          if (params.isInitialLoad) {
            isInitialFetchRef.current = false;
          }
        });
      } catch (error) {
        // If the fetch was aborted, don't show an error
        if ((error as any)?.name !== 'AbortError') {
          console.error('Error fetching venues from backend:', error);

          // Track error event
          trackEvent('search_error', {
            error: (error as Error).message,
            date: params.date,
            time: params.time || 'not-set',
            people: params.people
          });
        }
      } finally {
        setIsLoading(false);
        isFetchingRef.current = false;
      }
    }, 1000), // Increased debounce time to 1000ms for better handling of rapid input changes
    [trackSearch] // Add trackSearch to dependencies
  );

  // Use effect for handling initial load from URL parameters
  useEffect(() => {
    if (props.searchParams && initialLoadRef.current) {
      console.log('Initializing search parameters from URL');
      console.log('Original search params:', props.searchParams);
      console.log('Privacy level from URL:', props.searchParams.privacyLevel);

      // Convert SearchParams to URLSearchParams
      const urlSearchParams = new URLSearchParams();

      // Add parameters to URLSearchParams if they exist
      if (props.searchParams.date) urlSearchParams.set('date', props.searchParams.date);
      if (props.searchParams.time) urlSearchParams.set('time', props.searchParams.time);
      if (props.searchParams.people) urlSearchParams.set('size', props.searchParams.people.toString());
      if (props.searchParams.zipCode) urlSearchParams.set('zip', props.searchParams.zipCode);
      if (props.searchParams.attributes && props.searchParams.attributes.length > 0) {
        urlSearchParams.set('attributes', props.searchParams.attributes.join(','));
      }
      // Add neighborhoods to URL params
      if (props.searchParams.neighborhoods && props.searchParams.neighborhoods.length > 0) {
        urlSearchParams.set('neighborhoods', props.searchParams.neighborhoods.join(','));
        console.log('Setting neighborhoods in URL params:', props.searchParams.neighborhoods.join(','));
      }
      // Add privacy level to URL params
      if (props.searchParams.privacyLevel) {
        urlSearchParams.set('privacyLevel', props.searchParams.privacyLevel);
        console.log('Setting privacy level in URL params:', props.searchParams.privacyLevel);
      }

      // Handle price range - can come as either priceRange array or minPrice/maxPrice
      if (props.searchParams.priceRange) {
        urlSearchParams.set('price', `${props.searchParams.priceRange[0]}-${props.searchParams.priceRange[1]}`);
      } else if (props.searchParams.minPrice !== undefined && props.searchParams.maxPrice !== undefined) {
        urlSearchParams.set('price', `${props.searchParams.minPrice}-${props.searchParams.maxPrice}`);
      }

      // Initialize store with URL parameters
      initializeFromUrl(urlSearchParams);
      initialLoadRef.current = false;

      // Log the parameters that will be used for the fetch
      console.log('Neighborhoods from search params:', props.searchParams.neighborhoods);
      console.log('Privacy level before fetch:', props.searchParams.privacyLevel);

      // Trigger the initial fetch using our debounced function
      setIsLoading(true);

      let abortController: AbortController | null = null;

      // Use a callback to set the abort controller
      debouncedFetch({
        date: props.searchParams.date || '',
        time: props.searchParams.time || null,
        people: props.searchParams.people?.toString() || '',
        attributes: props.searchParams.attributes || [],
        priceRange: props.searchParams.priceRange
          ? `${props.searchParams.priceRange[0]}-${props.searchParams.priceRange[1]}`
          : props.searchParams.minPrice !== undefined && props.searchParams.maxPrice !== undefined
            ? `${props.searchParams.minPrice}-${props.searchParams.maxPrice}`
            : '',
        zipCode: props.searchParams.zipCode || '',
        neighborhoods: props.searchParams.neighborhoods || [],
        privacyLevel: props.searchParams.privacyLevel,  // Make sure this is passed correctly
        isInitialLoad: true,
      }, (controller) => {
        abortController = controller;
      });

      // Cleanup function to abort fetch if component unmounts during initialization
      return () => {
        // Safely abort any in-progress fetch
        if (abortController) {
          try {
            // Only abort if not already aborted
            if (!abortController.signal.aborted) {
              abortController.abort();
            }
          } catch (error) {
            console.log('Abort error:', error);
          }
        }
      };
    }
  }, [props.searchParams, initializeFromUrl, debouncedFetch]);

  // Handle venue fetching, but prevent duplicate requests on initial load
  useEffect(() => {
    // Skip this effect during initial URL parameter processing
    if (isInitialFetchRef.current) {
      return;
    }

    // Use the debounced fetch function to prevent duplicate calls
    const dateStr = selectedDate ? selectedDate.format('YYYY-MM-DD') : '';

    // Add explicit debug
    console.log('Current privacy level from store:', privacyLevel);

    let abortController: AbortController | null = null;

    // Call the debounced fetch with current parameters
    debouncedFetch({
      date: dateStr,
      time: selectedTime,
      people: people || '',
      attributes: selectedAttributes || [],
      priceRange: priceRange || '',
      zipCode: zipCode || '',
      neighborhoods: neighborhoods || [],
      privacyLevel: privacyLevel, // Ensure this is correct
      isInitialLoad: false
    }, (controller) => {
      abortController = controller;
    });

    // Return cleanup function
    return () => {
      // Safely abort any in-progress fetch when unmounting or parameters change
      if (abortController) {
        try {
          // Only abort if the controller is valid and not already aborted
          if (!abortController.signal.aborted) {
            abortController.abort();
          }
        } catch (error) {
          console.log('Abort error:', error);
        }
      }
    };
  }, [selectedDate, selectedTime, people, selectedAttributes, priceRange, zipCode, neighborhoods, privacyLevel, debouncedFetch]);

  // Combined useEffect for document title and currentDate
  useEffect(() => {
    // Convert dayjs object to Date
    const currentDateValue = selectedDate ? selectedDate.toDate() : new Date();
    setCurrentDate(currentDateValue);
    document.title = `Search Venues - ${currentDateValue.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} - Litty`;
  }, [selectedDate]);

  // New effect to set loading state when filters change
  useEffect(() => {
    // If filters have changed (isDirty is true), show loading state immediately
    if (isDirty) {
      setIsLoading(true);
    }
  }, [isDirty, selectedDate, selectedTime, people, selectedAttributes, priceRange, zipCode]);

  // Use layout effect to set map center from the first venue's location
  useEffect(() => {
    if (venues.length > 0 && venues[0].Position && !mapCenter) {
      setMapCenter([
        venues[0].Position.lat,
        venues[0].Position.lng,
      ]);
    }
  }, [venues, mapCenter]);


  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);

    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  useEffect(() => {
    if (!tempHoveredVenueId) {
      setHoveredVenueId(null);
      setHoveredVenue(null);
      return;
    }

    const timeout = setTimeout(() => {
      setHoveredVenueId(tempHoveredVenueId);
      const venue = venues.find(v => v.ID === tempHoveredVenueId) ||
        allVenues.find(v => v.ID === tempHoveredVenueId);
      setHoveredVenue(venue || null);
    }, 100);

    return () => clearTimeout(timeout);
  }, [tempHoveredVenueId, venues, allVenues]);

  const handleVenueHover = (venueId: string | null) => {
    setTempHoveredVenueId(venueId);

    // Only track when hovering onto a venue, not off
    if (venueId) {
      const venue = venues.find(v => v.ID === venueId) ||
        allVenues.find(v => v.ID === venueId);

      if (venue) {
        trackVenueInteraction('hover', venue.ID, venue.Name);
      }
    }
  };

  // Enhance handleCenterMap to track interactions
  const handleCenterMap = useCallback((venue: Venue) => {
    if (venue.Position && venue.Position.lat && venue.Position.lng) {
      // Set the map center and zoom
      setMapCenter([venue.Position.lat, venue.Position.lng]);
      setMapZoom(15);

      // Track the map centering action
      trackVenueInteraction('center_map', venue.ID, venue.Name);
    }
  }, [trackVenueInteraction]);

  // Effect to load the rest of the venues after initial ones are rendered
  useEffect(() => {
    if (initialVenuesLoaded && fullVenuesDataRef.current) {
      const data = fullVenuesDataRef.current;

      // Set loading state
      setIsLoadingFullResults(true);

      // Use requestAnimationFrame to ensure we're not blocking the UI thread
      const frameId = requestAnimationFrame(() => {
        // Set a timeout to give browser time to render the initial venues
        const timeoutId = setTimeout(() => {
          // Update the state in a batch to reduce renders
          const update = () => {
            setAllVenues(data.allVenues);
            setVenues(data.venues); // This now contains privacy-filtered venues
            setVenuePrices(data.venuePrices);
            setVenueBookedTimeSlots(data.venueBookedTimeSlots);
            setIsLoadingFullResults(false);
            setInitialVenuesLoaded(false); // Reset for future searches

            // Log the privacy level filtering stats (if applicable)
            if (privacyLevel) {
              console.log(`Loaded ${data.venues.length} venues with ${privacyLevel} privacy level out of ${data.allVenues.length} total venues`);
            }
          };

          // Use React batching for updates
          update();
        }, 300); // Slightly longer timeout for better visibility

        return () => {
          clearTimeout(timeoutId);
        };
      });

      return () => {
        cancelAnimationFrame(frameId);
      };
    }
  }, [initialVenuesLoaded, privacyLevel]);

  // Calculate displayed venues whenever filters or map bounds change
  useEffect(() => {
    // Use a ref to track if venues have changed meaningfully
    const filteredVenues = isMapFiltered && visibleVenueIds.length > 0
      ? venues.filter(venue => visibleVenueIds.includes(venue.ID))
      : venues;

    // Only update displayedVenues if the filtered result is different
    // This prevents unnecessary re-renders
    if (JSON.stringify(filteredVenues.map(v => v.ID)) !==
      JSON.stringify(displayedVenues.map(v => v.ID))) {
      setDisplayedVenues(filteredVenues);
    }
  }, [venues, visibleVenueIds, isMapFiltered, displayedVenues]);

  // Enhanced handleMapBoundsChange to track map filtering
  const handleMapBoundsChange = useCallback((newVisibleVenueIds: string[]) => {
    // Only update if the IDs have actually changed to prevent rendering loops
    if (JSON.stringify(newVisibleVenueIds) !== JSON.stringify(visibleVenueIds)) {
      setVisibleVenueIds(newVisibleVenueIds);

      // Track map bounds change when it filters venues
    }
  }, [visibleVenueIds, isMapFiltered, venues.length, trackSearch, selectedDate, selectedTime, people, selectedAttributes, priceRange, zipCode, neighborhoods]);

  // Add tracking for mobile map toggle
  const handleMapToggle = useCallback(() => {
    setShowMap(!showMap);
  }, [showMap, displayedVenues.length]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100%',
        bgcolor: '#1C1C34'
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          width: '100%',
          maxWidth: '1920px',
          margin: '0 auto',
          bgcolor: '#1C1C34'
        }}
      >
        <SearchNavBar />
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          width: '100%',
          maxWidth: '1920px',
          margin: '0 auto',
          overflow: 'hidden'
        }}
      >
        {/* Map View */}
        {(!isMobile || showMap) && (
          <Box
            sx={{
              flex: { xs: 1, md: 0.4, lg: 0.5 },
              height: { xs: '100%', md: 'calc(100vh - 64px)' },
              display: 'flex',
              flexDirection: 'column',
              pr: { xs: 0, md: 2 },
              position: { xs: 'relative', md: 'sticky' },
              top: { md: '64px' }
            }}
          >
            <BetterMap
              venues={venues.length > 0 ? venues : []}
              otherVenues={allVenues.filter(v => !venues.some(venue => venue.ID === v.ID))}
              venueBookedTimeSlots={venueBookedTimeSlots}
              hoveredVenueId={hoveredVenueId}
              venuePrices={venuePrices}
              date={currentDate.toISOString()}
              time={selectedTime}
              size={people ? parseInt(people) : 10}
              selectedVenue={hoveredVenue}
              onBoundsChange={handleMapBoundsChange}
              isMapFiltered={isMapFiltered}
            />
          </Box>
        )}

        {/* Venue List */}
        {(!isMobile || !showMap) && (
          <Box
            sx={{
              flex: { xs: 1, md: 0.6, lg: 0.5 },
              height: { xs: 'calc(100vh - 64px)', md: 'calc(100vh - 64px)' },
              overflow: 'auto',
              pb: { xs: 8, md: 0 }, // Add padding at bottom for mobile toggle button
              px: { xs: 1, md: 2 }
            }}
          >
            <VenueCardList
              venues={displayedVenues}
              allVenues={allVenues}
              venueBookedTimeSlots={venueBookedTimeSlots || {}}
              currentDate={currentDate}
              people={people}
              selectedTime={selectedTime}
              onVenueHover={handleVenueHover}
              onCenterMap={handleCenterMap}
              isLoading={isLoading}
              isPending={isPending}
              isLoadingFullResults={isLoadingFullResults}
              isMapFiltered={isMapFiltered}
              visibleVenueIds={visibleVenueIds}
              venuePrices={venuePrices}
              privacyLevel={privacyLevel as 'private' | 'semi-private' | 'non-private'}
            />
          </Box>
        )}
      </Box>

      {/* Mobile Venue Info - Only rendered when needed */}
      {isMobile && selectedVenue && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 16,
            left: 16,
            right: 16,
            zIndex: 999,
            backgroundColor: 'white',
            borderRadius: 2,
            boxShadow: 3,
          }}
        >
          {/* <VenueFloatingCard venue={selectedVenue} isDesktop={!isMobile} onClose={() => setSelectedVenue(null)} /> */}
        </Box>
      )}

      {/* Mobile Toggle Button */}
      {isMobile && (
        <Button
          onClick={handleMapToggle}
          startIcon={showMap ? <ListIcon /> : <MapIcon />}
          sx={{
            position: 'fixed',
            bottom: 32,
            right: 16,
            zIndex: 1000,
            backgroundColor: '#D1C4E9',
            color: 'black',
            padding: '8px 16px',
            borderRadius: '28px',
            boxShadow: '0px 3px 10px rgba(0,0,0,0.15)',
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '14px',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            minWidth: '140px',
            justifyContent: 'center',
            '&:hover': {
              backgroundColor: '#C5B6E0',
              transform: 'translateY(-2px)',
              boxShadow: '0px 4px 12px rgba(0,0,0,0.2)',
            },
            '&:active': {
              backgroundColor: '#C5B6E0',
              transform: 'translateY(0px)',
            },
          }}
        >
          {showMap
            ? <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography component="span" sx={{ mr: 0.5, fontWeight: 700 }}>Show</Typography>
              <Chip
                size="small"
                label={displayedVenues.length}
                sx={{
                  bgcolor: 'rgba(0,0,0,0.1)',
                  height: '20px',
                  fontSize: '12px',
                  fontWeight: 700,
                  margin: '0 4px'
                }}
              />
              <Typography component="span" sx={{ ml: 0.5, fontWeight: 700 }}>venues</Typography>
            </Box>
            : 'Show map'}
        </Button>
      )}
    </Box>
  );
}

export default function SearchPage(props: { searchParams: SearchParams }) {
  return (
    <Suspense fallback={<LoadingLogo />}>
      <SearchContent searchParams={props.searchParams} />
    </Suspense>
  );
}